import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Check, X, Clock, User as UserIcon, Hourglass } from "lucide-react";

/**
 * Formats the elapsed time between `dateStr` and now into a compact Turkish
 * string like "3 dk", "2 sa 15 dk" or "1 g 4 sa". Returns { label, tone } where
 * tone is fresh|warn|urgent based on how long the request has been waiting.
 */
const formatElapsed = (dateStr) => {
  if (!dateStr) return null;
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  let label;
  if (minutes < 1) label = "az önce";
  else if (minutes < 60) label = `${minutes} dk`;
  else if (hours < 24) {
    const rem = minutes % 60;
    label = rem ? `${hours} sa ${rem} dk` : `${hours} sa`;
  } else {
    const remH = hours % 24;
    label = remH ? `${days} g ${remH} sa` : `${days} g`;
  }

  let tone = "fresh";
  if (minutes >= 120) tone = "urgent";
  else if (minutes >= 30) tone = "warn";

  return { label, tone };
};

/**
 * Admin-only screen listing pending inventory deletion requests. Approve →
 * deletes the referenced inventory items and logs a movement per item.
 * Reject → marks the request rejected without touching inventory.
 */
export default function DeletionRequests() {
  // Trigger a re-render every 60s so elapsed-time badges stay fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = () => {
    setLoading(true);
    api
      .get("/deletion-requests", { params: { status } })
      .then(({ data }) => setItems(data.items))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user?.role === "supervisor") load();
  }, [status, user]);

  if (user?.role !== "supervisor") {
    return (
      <div data-testid="approvals-forbidden" className="hard border border-[color:var(--warning)] bg-[color:var(--warning-bg)] text-[color:var(--warning)] p-4">
        Bu sayfa yalnızca Supervisor içindir.
      </div>
    );
  }

  const resolve = async (id, action) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/deletion-requests/${id}/${action}`);
      if (action === "approve") {
        const n = data.deleted ?? data.entered ?? data.exited ?? data.updated ?? 0;
        const noun =
          data.type === "entry"
            ? "kayıt eklendi"
            : data.type === "exit"
              ? "kayıt çıkışı yapıldı"
              : data.type === "edit_date"
                ? "kaydın tarihi güncellendi"
                : "kayıt silindi";
        toast.success(`Onaylandı · ${n} ${noun}`);
      } else {
        toast.success("Talep reddedildi");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-4 flex flex-wrap items-center gap-3">
        <div className="ticker text-[color:var(--text-3)]">/// ONAY BEKLEYEN İŞLEMLER</div>
        <div className="flex hard border-2 border-[color:var(--border)] overflow-hidden ml-4">
          {[
            { v: "pending", label: "Bekleyen" },
            { v: "approved", label: "Onaylanan" },
            { v: "rejected", label: "Reddedilen" },
            { v: "all", label: "Tümü" },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setStatus(t.v)}
              data-testid={`req-filter-${t.v}`}
              className={`px-4 h-[42px] text-sm font-semibold uppercase tracking-wider ${
                status === t.v
                  ? "bg-[color:var(--primary)] text-white"
                  : "bg-white hover:bg-[color:var(--bg)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="ticker text-[color:var(--text-3)] ml-auto">
          {loading ? "Yükleniyor…" : `${items.length} talep`}
        </div>
      </div>

      {items.length === 0 && !loading && (
        <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-12 text-center text-[color:var(--text-3)]">
          <Clock size={32} className="mx-auto mb-2 opacity-40" />
          Bu durumda talep yok.
        </div>
      )}

      <div className="space-y-3">
        {items.map((req) => (
          <div
            key={req.id}
            data-testid="deletion-request-card"
            className="hard border-2 border-[color:var(--border)] bg-[color:var(--surface)]"
          >
            <div className="p-4 flex flex-wrap items-start justify-between gap-3 border-b border-[color:var(--border)]">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    data-testid="req-type-badge"
                    className={`inline-block px-2 py-0.5 hard text-[10px] font-bold uppercase ${
                      req.type === "entry"
                        ? "bg-[color:var(--success-bg)] text-[color:var(--success)]"
                        : req.type === "exit"
                          ? "bg-[color:var(--accent-bg)] text-[color:var(--accent)]"
                          : req.type === "edit_date"
                            ? "bg-[color:var(--primary)] text-white"
                            : "bg-[color:var(--error-bg)] text-[color:var(--error)]"
                    }`}
                  >
                    {req.type === "entry"
                      ? "GİRİŞ"
                      : req.type === "exit"
                        ? "ÇIKIŞ"
                        : req.type === "edit_date"
                          ? "TARİH DEĞİŞİKLİĞİ"
                          : "SİLME"}
                  </span>
                  <span
                    className={`inline-block px-2 py-0.5 hard text-[10px] font-bold uppercase ${
                      req.status === "pending"
                        ? "bg-[color:var(--warning-bg)] text-[color:var(--warning)]"
                        : req.status === "approved"
                          ? "bg-[color:var(--success-bg)] text-[color:var(--success)]"
                          : "bg-[color:var(--error-bg)] text-[color:var(--error)]"
                    }`}
                  >
                    {{ pending: "Bekliyor", approved: "Onaylandı", rejected: "Reddedildi" }[req.status] || req.status}
                  </span>
                  <span className="font-mono font-bold text-lg">
                    {req.count} kayıt
                  </span>
                  {req.status === "pending" && (() => {
                    const el = formatElapsed(req.created_at);
                    if (!el) return null;
                    const toneClass =
                      el.tone === "urgent"
                        ? "bg-[color:var(--error-bg)] text-[color:var(--error)] border-[color:var(--error)]"
                        : el.tone === "warn"
                          ? "bg-[color:var(--warning-bg)] text-[color:var(--warning)] border-[color:var(--warning)]"
                          : "bg-[color:var(--success-bg)] text-[color:var(--success)] border-[color:var(--success)]";
                    return (
                      <span
                        data-testid="req-wait-badge"
                        title={`Bekleme süresi: ${el.label}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 hard border text-[10px] font-bold uppercase tracking-wider ${toneClass}`}
                      >
                        <Hourglass size={10} strokeWidth={2.5} />
                        {el.label} bekliyor
                      </span>
                    );
                  })()}
                </div>
                <div className="text-xs text-[color:var(--text-2)] flex items-center gap-2">
                  <UserIcon size={12} />
                  {req.requester_name || req.requester_email} ·{" "}
                  {new Date(req.created_at).toLocaleString("tr-TR")}
                </div>
                {req.reason && (
                  <div className="text-sm mt-2">
                    <span className="ticker text-[10px] text-[color:var(--text-3)]">
                      Gerekçe:
                    </span>{" "}
                    {req.reason}
                  </div>
                )}
                {req.status !== "pending" && (
                  <div className="text-xs text-[color:var(--text-3)] mt-1">
                    {req.resolved_action === "approve" ? "Onaylayan" : "Reddeden"}:{" "}
                    {req.resolved_by} ·{" "}
                    {req.resolved_at
                      ? new Date(req.resolved_at).toLocaleString("tr-TR")
                      : ""}
                  </div>
                )}
              </div>
              {req.status === "pending" && (
                <div className="flex gap-2">
                  <button
                    data-testid="req-reject"
                    disabled={busyId === req.id}
                    onClick={() => resolve(req.id, "reject")}
                    className="px-4 h-10 hard border-2 border-[color:var(--border)] hover:border-[color:var(--error)] hover:text-[color:var(--error)] text-sm font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-40 transition-colors"
                  >
                    <X size={14} /> Reddet
                  </button>
                  <button
                    data-testid="req-approve"
                    disabled={busyId === req.id}
                    onClick={() => resolve(req.id, "approve")}
                    className="px-4 h-10 hard bg-[color:var(--error)] hover:opacity-90 text-white font-bold uppercase tracking-wider text-sm flex items-center gap-1 disabled:opacity-40 transition-opacity"
                  >
                    <Check size={14} strokeWidth={2.5} />{" "}
                    {req.type === "deletion" ? "Onayla & Sil" : "Onayla"}
                  </button>
                </div>
              )}
            </div>
            <button
              data-testid="req-expand-toggle"
              onClick={() =>
                setExpanded((e) => ({ ...e, [req.id]: !e[req.id] }))
              }
              className="w-full px-4 py-2 text-left text-xs ticker text-[color:var(--primary)] hover:bg-[color:var(--bg)]"
            >
              {expanded[req.id] ? "▼ Kayıtları gizle" : "▶ Kayıtları göster"}
            </button>
            {expanded[req.id] && (
              <div className="p-3 border-t border-[color:var(--border)]">
                <table className="dense-table w-full">
                  <thead>
                    <tr>
                      <th>Barkod</th>
                      <th>İsim</th>
                      <th>Ebat</th>
                      <th>Adet</th>
                      <th>Sehpa</th>
                      {req.type === "edit_date" && (
                        <>
                          <th>Eski Tarih</th>
                          <th>Yeni Tarih</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(req.snapshot?.length
                      ? req.snapshot
                      : (req.payload?.items || []).map((it) => ({
                          id: it.barkod,
                          barkod: it.barkod,
                          isim: it.isim || "-",
                          ebat: it.ebat || "-",
                          adet: it.adet,
                          sehpa_konumu: req.payload?.sehpa_konumu || req.payload?.musteri || "-",
                        }))
                    ).map((s) => (
                      <tr key={s.id || s.barkod}>
                        <td className="font-mono font-semibold">{s.barkod}</td>
                        <td className="max-w-xs truncate">{s.isim}</td>
                        <td className="font-mono">{s.ebat}</td>
                        <td className="font-mono font-bold">{s.adet}</td>
                        <td className="font-mono">{s.sehpa_konumu}</td>
                        {req.type === "edit_date" && (
                          <>
                            <td className="font-mono line-through text-[color:var(--text-3)]">{s.old_date || "-"}</td>
                            <td className="font-mono font-bold text-[color:var(--primary)]" data-testid="req-new-date">{s.new_date}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
